/**
 * Diaper UI API client.
 *
 * Wraps @nova-integration/ui-shared's createApiClient with the diaper-specific
 * Nova / events / accounts endpoints. All Nova calls go through diaper-ui's BFF
 * (/api/nova/*), which in turn proxies to ops-ui's /api/internal/nova/* — that
 * is where the nova_accounts credentials live.
 */
import { createApiClient, ApiError } from '@nova-integration/ui-shared';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const APP_AUTH_ERROR_HEADER = 'x-app-auth-error';

const client = createApiClient({ useAuthStore, baseUrl: API_BASE_URL });

export { ApiError };

export interface NovaRequestOptions {
  novaAccountId?: string | null;
}

export function novaProxyHeaders(novaAccountId?: string | null): Record<string, string> {
  return novaAccountId ? { 'x-nova-account-id': novaAccountId } : {};
}

interface ApiCallOptions extends RequestInit {
  skipAuth?: boolean;
}

export async function apiCall(endpoint: string, options: ApiCallOptions = {}): Promise<Response> {
  return client.call(endpoint, options);
}

async function getErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const ct = response.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const body = await response.json();
      if (typeof body?.message === 'string' && body.message.trim()) return body.message;
      if (typeof body?.error === 'string' && body.error.trim()) return body.error;
    }
    const text = await response.text();
    if (text.trim()) return text;
  } catch {
    /* ignore */
  }
  return fallback;
}

export async function loadSessions(
  siteIds: number | number[],
  options: NovaRequestOptions = {}
): Promise<{ statuses: any[] }> {
  const siteIdsParam = Array.isArray(siteIds) ? siteIds.join(',') : siteIds;
  const response = await apiCall(
    `/api/nova/_ah/api/monitoring/v5/session-status-sites?siteIds=${siteIdsParam}`,
    { headers: novaProxyHeaders(options.novaAccountId) }
  );
  if (!response.ok) {
    throw new ApiError(await getErrorMessage(response, 'Failed to load sessions'), response.status);
  }
  return response.json();
}

export async function createSession(
  data: { siteId: number; clipAddress: string; clipName: string; client: string; sex?: string; criterionId?: number },
  options: NovaRequestOptions = {}
): Promise<number> {
  const response = await apiCall('/api/nova/_ah/api/monitoring/v5/session', {
    method: 'POST',
    body: JSON.stringify(data),
    headers: novaProxyHeaders(options.novaAccountId),
  });
  if (!response.ok) {
    throw new ApiError(await response.text() || 'Failed to create session', response.status);
  }
  return response.json();
}

export async function deleteSession(sessionId: number, options: NovaRequestOptions = {}): Promise<void> {
  const response = await apiCall(`/api/nova/_ah/api/monitoring/v5/session/${sessionId}/close`, {
    method: 'POST',
    headers: novaProxyHeaders(options.novaAccountId),
  });
  if (!response.ok) {
    throw new ApiError(await response.text() || 'Failed to delete session', response.status);
  }
}

export async function sendSensorPacket(
  data: {
    payload: number[];
    time: string;
    rssi: number;
    senderAddress: string;
    receiverAddress: string;
    receiverAndroidId: string;
  },
  options: NovaRequestOptions = {}
): Promise<void> {
  const response = await apiCall('/api/nova/_ah/api/networking/v0.10/sensor-packet', {
    method: 'POST',
    body: JSON.stringify(data),
    headers: novaProxyHeaders(options.novaAccountId),
  });
  if (!response.ok) {
    throw new ApiError('Failed to send sensor packet', response.status);
  }
}

export async function loadDiaperModels(options: NovaRequestOptions = {}): Promise<any[]> {
  // Diaper-models is a Nova-install global catalog, but the proxy is account-aware
  // for credential resolution: we always pass the chosen Nova account so the BFF
  // knows which JSESSIONID to use. Callers must provide novaAccountId; passing
  // nothing yields a 4xx from the proxy (intentional — no silent fallbacks).
  const response = await apiCall('/api/nova/_ah/api/monitoring/v5/diaper-models', {
    headers: novaProxyHeaders(options.novaAccountId),
  });
  if (!response.ok) {
    throw new ApiError(await getErrorMessage(response, 'Failed to load diaper models'), response.status);
  }
  return response.json();
}

export async function loadAllCriteria(
  siteId: number,
  options: NovaRequestOptions = {}
): Promise<Array<{ id: number; name: string }>> {
  const response = await apiCall(`/api/nova/_ah/api/admin/v3/site/${siteId}/visibleCriteria`, {
    headers: novaProxyHeaders(options.novaAccountId),
  });
  if (!response.ok) {
    throw new ApiError(await getErrorMessage(response, 'Failed to load criteria'), response.status);
  }
  return response.json();
}

export async function loadCriterion(criterionId: number, options: NovaRequestOptions = {}): Promise<any> {
  const response = await apiCall(`/api/nova/_ah/api/admin/v3/criterion/${criterionId}`, {
    headers: novaProxyHeaders(options.novaAccountId),
  });
  if (!response.ok) {
    throw new ApiError(await getErrorMessage(response, 'Failed to load criterion'), response.status);
  }
  return response.json();
}

export async function loadSessionsFromEvents(): Promise<{
  sessions: Array<{
    id: number;
    client: string;
    clipName: string;
    clipAddress: string;
    monitored: boolean;
    sessionStartTime: string | null;
    lastEventTime: string;
    eventCount: number;
    siteId: number | null;
    diaperModelName: string | null;
  }>;
  count: number;
  scanned_events: number;
  organization_id: number;
}> {
  const response = await apiCall('/api/events?mode=sessions');
  if (!response.ok) {
    throw new ApiError('Failed to load sessions from events', response.status);
  }
  return response.json();
}

export async function loadEvents(options: { limit?: number; sessionId?: number | string; after?: string } = {}): Promise<{
  events: any[];
  count?: number;
  unseen_count?: number;
  organization_id?: number;
  has_more?: boolean;
  requested_limit?: number;
  filters?: { sessionId?: string | null; after?: string | null };
}> {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit || 100));
  if (options.sessionId) params.set('sessionId', String(options.sessionId));
  if (options.after) params.set('after', options.after);

  const response = await apiCall(`/api/events?${params.toString()}`);
  if (!response.ok) {
    throw new ApiError('Failed to load events', response.status);
  }
  return response.json();
}

export async function replaceClip(
  sessionId: number,
  data: { address: string; name: string },
  options: NovaRequestOptions = {}
): Promise<void> {
  const response = await apiCall(`/api/nova/_ah/api/monitoring/v5/session/${sessionId}/clip`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: novaProxyHeaders(options.novaAccountId),
  });
  if (!response.ok) {
    throw new ApiError(await response.text() || 'Failed to replace clip', response.status);
  }
}

export async function toggleAwayStatus(
  data: {
    diaperId: number | null;
    sessionId: number;
    tagger: string | null;
    tags: Array<{ name: string; value: string }>;
  },
  options: NovaRequestOptions = {}
): Promise<void> {
  const response = await apiCall('/api/nova/_ah/api/admin/v3/persistent-patient-tags', {
    method: 'POST',
    body: JSON.stringify(data),
    headers: novaProxyHeaders(options.novaAccountId),
  });
  if (!response.ok) {
    throw new ApiError('Failed to update away status', response.status);
  }
}

// Suppress unused import warning. The header constant is kept for parity with
// ops-ui's api.ts and may be needed if we add ad-hoc fetches outside the client.
void APP_AUTH_ERROR_HEADER;
